/**
 * Calculates what the next position from start should be until
 * the given distance is complete, based on the amount of time elapsed,
 * and using a easing out quint timing function (used internally
 * by the smoothScrollPolyfill).
 *
 * @param elapsed - amount of time elapsed
 */
const easingOutQuint = (
  elapsed: number,
  start: number,
  distance: number,
  duration: number,
): number => {
  const elapsedOverDuration = elapsed / duration - 1;
  return (
    start +
    distance *
      (elapsedOverDuration *
        elapsedOverDuration *
        elapsedOverDuration *
        elapsedOverDuration *
        elapsedOverDuration +
        1)
  );
};

/**
 * @param node - The scrollable element container
 * @param key - Scroll property (scrollTop or scrollLeft);
 * @param target - The scroll end location in pixels
 * @param callback - Called after the scroll reached the end location
 */
function smoothScrollPolyfill(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node: any,
  key: string,
  target: number,
  callback: () => void,
): () => void {
  const startTime = Date.now();
  const offset = node[key];
  const gap = target - offset;
  const duration = 1000;
  let interrupt = false;

  const step = (): void => {
    const elapsed = Date.now() - startTime;
    const percentage = elapsed / duration;

    if (interrupt) {
      return;
    }

    if (percentage >= 1) {
      cleanup();
      callback && callback();
      return;
    }

    node[key] = easingOutQuint(elapsed, offset, gap, duration);
    window.requestAnimationFrame(step);
  };

  const cancel = (): void => {
    interrupt = true;
    cleanup();
  };

  const cleanup = (): void => {
    node.removeEventListener('wheel', cancel);
    node.removeEventListener('touchstart', cancel);
  };

  node.addEventListener('wheel', cancel, { passive: true });
  node.addEventListener('touchstart', cancel, { passive: true });

  step();

  return cancel;
}

/**
 * Check if the browser supports scroll behavior smooth
 */
function testSupportsSmoothScroll(): boolean {
  let supports = false;

  try {
    const div = document.createElement('div');
    div.scrollTo({
      top: 0,
      get behavior(): 'auto' | 'smooth' {
        supports = true;
        return 'smooth';
      },
    });
  } catch (e) {
    // Edge throws error
  }

  return supports;
}

/**
 * Set focus to the anchor element
 */
const focusAnchor = (anchor: HTMLElement, endLocation: number): void => {
  anchor.focus();
  if (document.activeElement !== anchor) {
    anchor.setAttribute('tabindex', '-1');
    anchor.focus();
    anchor.style.outline = 'none';
  }

  window.scrollTo(0, endLocation);
};

/**
 * Calculates the scroll position in pixels of the anchor
 */
const getEndLocation = (
  anchorElement: HTMLElement | null,
  offset: number,
): number => {
  let anchor = anchorElement;
  let location = 0;
  if (anchor && anchor.offsetParent) {
    do {
      location += anchor.offsetTop;
      anchor = anchor.offsetParent as HTMLElement;
    } while (anchor);
  }

  location = Math.max(location - offset, 0);
  return location;
};

const hasNativeSmoothScroll = testSupportsSmoothScroll();

/**
 * Smooth scroll polyfill for browsers that don't support
 * the scroll behavior smooth option.
 *
 * @param node - The scrollable element container
 * @param anchor - The anchor to scroll to
 * @param offset - Number of pixels by which to offset scroll
 * @param horizontal - Indicates if this is a horizontal scroll
 */
export const smoothScroll = (
  node: HTMLElement,
  anchor: HTMLElement,
  offset = 0,
  horizontal = false,
): number | (() => void) => {
  const endPosition = getEndLocation(anchor, offset);

  if (hasNativeSmoothScroll) {
    window.addEventListener(
      'scroll',
      function onScroll() {
        if (window.pageYOffset !== endPosition) {
          return;
        }

        const options: AddEventListenerOptions & EventListenerOptions = {
          passive: true,
        };

        window.removeEventListener('scroll', onScroll, options);
        focusAnchor(anchor, endPosition);
      },
      { passive: true },
    );

    return window.requestAnimationFrame(() =>
      node.scrollTo({
        [horizontal ? 'left' : 'top']: endPosition,
        behavior: 'smooth',
      }),
    );
  }

  return smoothScrollPolyfill(
    node,
    horizontal ? 'scrollLeft' : 'scrollTop',
    endPosition,
    () => focusAnchor(anchor, endPosition),
  );
};
